import { Button } from '@components/primitives/Button';
import { Box, Skeleton } from '@mui/material';
import type { UserNoteTypeII } from '@ts/types';

import { getNoteRoute } from '@utils/noteRouting';

import { EmptyState } from './EmptyState';
import { NoteItem } from './NoteItem';

interface NotesListProps {
  notes: UserNoteTypeII[] | null | undefined;
  onNoteClick: (noteId: string, link: string) => void;
  onViewAllNotes: () => void;
  onViewAll?: () => void;
  loading?: boolean;
}

/**
 * Pure UI component for rendering a list of notes
 * Handles loading, empty, and populated states
 */
export const NotesList: React.FC<NotesListProps> = ({
  notes,
  onNoteClick,
  onViewAllNotes,
  onViewAll,
  loading = false,
}) => {
  // Loading state - show skeletons
  if (loading || notes === undefined) {
    return (
      <Box>
        {Array.from({ length: 3 }).map((_, index) => (
          <Box
            key={index}
            sx={{
              px: 3,
              py: 2,
              borderTop: index === 0 ? 'none' : '1px solid',
              borderColor: 'borderColors.primary',
            }}
          >
            <Skeleton variant="text" sx={{width: "60%", height: 24,  mb: 0.5 }} />
            <Skeleton variant="text" sx={{width: "40%", height: 20,  mb: 0.5 }} />
            <Skeleton variant="text"  sx={{ width: "100%", height: 20 }}/>
            <Skeleton variant="text" sx={{width: "90%", height: 20,  mb: 0.5 }} />
            <Skeleton variant="text"  sx={{ width: "30%", height: 16 }}/>
          </Box>
        ))}
      </Box>
    );
  }

  // Empty state
  if (!notes || notes.length === 0) {
    return <EmptyState onViewAllNotes={onViewAllNotes} />;
  }

  // Populated state - render notes
  return (
    <Box>
      {notes.map((note, index) => (
        <NoteItem
          key={note.note_id}
          title={note.note_title}
          patientName={note.patient_name}
          timestamp={note.time_uploaded}
          summary={note.summary}
          isFirst={index === 0}
          onClick={() =>
            onNoteClick(
              note.note_id,
              getNoteRoute({
                note_id: note.note_id,
                thread_id: (note as { thread_id?: string | null }).thread_id,
                status: note.status,
              }),
            )
          }
        />
      ))}

      {/* View All button at end of list */}
      {onViewAll && (
        <Box
          sx={{
            px: 3,
            py: 2,
            borderTop: '1px solid',
            borderColor: 'borderColors.primary',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Button variant="outlined" onClick={onViewAll}>View All Notes</Button>
        </Box>
      )}
    </Box>
  );
};
